import React from 'react';
import {
    Box, Skeleton, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';

/**
 * Preload skeletons for the Carriers (Global Carriers) view.
 *
 * Two exports, so the same shimmer covers both loading phases the user sees:
 *   - CarriersPageSkeleton      — route-level Suspense fallback while the lazy chunk downloads
 *                                 (mirrors the whole page shell: title, catalog header, filters, table).
 *   - GlobalCarrierTableSkeleton — in-content fallback while Firestore data fetches
 *                                 (just the count line + the 9-column catalog table).
 *
 * Kept dependency-light (MUI only) so it can be statically imported into App.js as a
 * Suspense fallback without dragging the carriers bundle into the main chunk.
 */

// Table skeleton that mirrors the Global Carriers catalog table
// (Carrier | Code | Shipment Type | Integration | Ships From | Ships To | Used by | Status | Actions).
export const GlobalCarrierTableSkeleton = ({ rows = 8 }) => (
    <>
        <Box sx={{ mb: 1 }}>
            <Skeleton variant="text" width={120} sx={{ fontSize: '12px' }} />
        </Box>
        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { fontSize: '12px' } }}>
                <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f8fafc', color: '#374151' } }}>
                        <TableCell>Carrier</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Shipment Type</TableCell>
                        <TableCell>Integration</TableCell>
                        <TableCell>Ships From</TableCell>
                        <TableCell>Ships To</TableCell>
                        <TableCell align="center">Used by</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Array.from({ length: rows }).map((_, i) => (
                        <TableRow key={i}>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Skeleton variant="circular" width={26} height={26} />
                                    <Skeleton variant="text" width={140} />
                                </Box>
                            </TableCell>
                            <TableCell><Skeleton variant="text" width={110} /></TableCell>
                            <TableCell><Skeleton variant="rounded" width={54} height={18} /></TableCell>
                            <TableCell><Skeleton variant="rounded" width={54} height={18} /></TableCell>
                            <TableCell><Skeleton variant="rounded" width={30} height={18} /></TableCell>
                            <TableCell><Skeleton variant="rounded" width={30} height={18} /></TableCell>
                            <TableCell align="center"><Skeleton variant="rounded" width={40} height={18} sx={{ mx: 'auto' }} /></TableCell>
                            <TableCell align="center"><Skeleton variant="rounded" width={34} height={18} sx={{ mx: 'auto' }} /></TableCell>
                            <TableCell align="right"><Skeleton variant="circular" width={20} height={20} sx={{ ml: 'auto' }} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    </>
);

// Full-page shell skeleton — used as the route Suspense fallback so the carriers view
// never flashes a bare centered spinner on a cold load.
export const CarriersPageSkeleton = () => (
    <Box sx={{ backgroundColor: 'transparent', width: '100%', height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header (matches the "Carriers" title block) */}
            <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                <Skeleton variant="text" width={130} sx={{ fontSize: '1.5rem', mb: 1 }} />
                <Skeleton variant="text" width={220} sx={{ fontSize: '12px' }} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, px: 2 }}>
                <Box sx={{ mt: 2 }}>
                    {/* Global Carriers header + Add button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <Skeleton variant="text" width={160} sx={{ fontSize: '15px' }} />
                            <Skeleton variant="text" width={340} sx={{ fontSize: '12px' }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Skeleton variant="rounded" width={150} height={32} />
                            <Skeleton variant="rounded" width={140} height={32} />
                        </Box>
                    </Box>

                    {/* Search + filter bar */}
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Skeleton variant="rounded" height={40} sx={{ flex: 1, minWidth: 220 }} />
                        <Skeleton variant="rounded" width={130} height={40} />
                        <Skeleton variant="rounded" width={150} height={40} />
                        <Skeleton variant="rounded" width={130} height={40} />
                        <Skeleton variant="rounded" width={140} height={40} />
                    </Box>

                    <GlobalCarrierTableSkeleton />
                </Box>
            </Box>
        </Box>
    </Box>
);

export default CarriersPageSkeleton;
